/**
 * Pure helpers for the Activity Center "Reply as" user selector (EAI-1530).
 *
 * The selector lets an agent choose which user a reply is attributed to. The
 * list is displayed in a fixed priority order, de-duplicated so each user
 * appears exactly once at the highest-priority slot they qualify for:
 *
 *   1. Logged-in user            (always index 0 — the default selection)
 *   2. Most recent poster        (author of the most recent user-sent message)
 *   3. Assigned opportunity users (primary type first, then the rest)
 *   4. Other conversation participants (any remaining message authors)
 *
 * These functions are intentionally free of React/Redux so they can be unit
 * tested in isolation and reused by any surface (web, extension).
 */

/** Context tag driving the label shown next to each user in the popover. */
export const REPLY_AS_CONTEXT = Object.freeze({
  YOU: "you",
  RECENT: "recent",
  ASSIGNED: "assigned",
  PARTICIPANT: "participant",
});

/**
 * Manager "Default Reply Rule" (EAI-1530). A rule resolves dynamically to a
 * concrete user per lead. Stored per-manager in user.settings.
 */
export const REPLY_RULE = Object.freeze({
  ME: "ME",
  RECENT: "RECENT",
  BDC: "BDC",
  PRIMARY_ASSIGNED: "PRIMARY_ASSIGNED",
  SECONDARY_ASSIGNED: "SECONDARY_ASSIGNED",
});

/**
 * Backend rule_type values for the saved default preference (EAI-1530,
 * saveReplyAsPreference). SELF = logged-in user, MOST_RECENT = last human
 * sender, OPPORTUNITY_USER_TYPE = the assigned user of a given userType.
 */
export const REPLY_RULE_TYPE = Object.freeze({
  SELF: "SELF",
  MOST_RECENT: "MOST_RECENT",
  OPPORTUNITY_USER_TYPE: "OPPORTUNITY_USER_TYPE",
});

/** Ordered options for the rule dropdown (label keys resolved via i18n in UI). */
export const REPLY_RULE_OPTIONS = Object.freeze([
  { value: REPLY_RULE.ME, label: "Me" },
  { value: REPLY_RULE.RECENT, label: "Recent user" },
  { value: REPLY_RULE.BDC, label: "BDC" },
  { value: REPLY_RULE.PRIMARY_ASSIGNED, label: "Primary Assigned" },
  { value: REPLY_RULE.SECONDARY_ASSIGNED, label: "Secondary Assigned" },
]);

// Substring matched (case-insensitive) against leadOpportunityUsers[].userType.name.
// Mirrors the canonical DS/SF strings ("primary assigned", "secondary assigned",
// "bdc"/"bdc rep") used by opportunityLayer.js::handleOwnerData.
const RULE_ROLE_TOKEN = Object.freeze({
  [REPLY_RULE.BDC]: "bdc",
  [REPLY_RULE.PRIMARY_ASSIGNED]: "primary",
  [REPLY_RULE.SECONDARY_ASSIGNED]: "secondary",
});

// A userType is treated as the primary assigned owner when its name contains
// "primary" (e.g. DealerSocket's "primary assigned"). Mirrors the existing
// owner-priority convention in opportunityLayer.js::handleOwnerData.
const isPrimaryUserType = userType => (userType?.name || "").toLowerCase().includes("primary");

const toId = value => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

const fullNameOf = user => {
  if (!user) return "";
  if (user.fullName && user.fullName.trim()) return user.fullName.trim();
  return `${user.firstName || ""} ${user.lastName || ""}`.trim();
};

/**
 * Normalize any user-bearing source (context user, message.user,
 * leadOpportunityUsers[].user) into the flat entry the selector renders.
 * Returns null when there's no resolvable user id, so callers can drop it.
 */
const toEntry = (user, { contextKey, roleName, assignmentType = "", isPrimary = false } = {}) => {
  const id = toId(user?.id);
  if (id === null) return null;
  const name = fullNameOf(user);
  return {
    id,
    userId: id,
    fullName: name || "Unknown user",
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    profilePic: user.profilePic || null,
    roleName: roleName || user.companyRole?.name || "",
    // Assignment context (leadOpportunityUsers[].userType.name) — e.g. "BDC",
    // "Primary Assigned". Drives the "{companyRole} - {assignmentType}" subtitle.
    assignmentType: assignmentType || "",
    contextKey,
    isPrimary,
  };
};

/**
 * Find the author of the most recent user-sent message.
 *
 * "User-sent" = the message carries a real user (message.userId / user.id),
 * which excludes inbound lead messages and Otto/system rows that have no user.
 * Ordered by dateSent desc; falls back to array order (assumed chronological)
 * when timestamps are missing.
 *
 * Identity is keyed on the message's ATTRIBUTED ``userId`` (the display /
 * reply-as user), NOT the nested ``user`` object — the backend populates
 * ``message.user`` with the authenticated (login) sender on reply-as sends, so
 * it can point at the wrong person. When a ``pool`` of known user records is
 * supplied, the attributed id is resolved to its current record (correct name /
 * photo); otherwise the nested ``user`` is used only when it matches the
 * attributed id, falling back to a minimal ``{ id }`` identity.
 *
 * @param {Array} messages
 * @param {Array} [pool] known user records to resolve the attributed id against
 * @returns {Object|null} the most recent poster's user record, or null
 */
export const getMostRecentPoster = (messages, pool = []) => {
  if (!Array.isArray(messages) || messages.length === 0) return null;
  const time = m => {
    const t = m?.dateSent ? new Date(m.dateSent).getTime() : NaN;
    return Number.isNaN(t) ? null : t;
  };
  let best = null;
  messages.forEach((m, index) => {
    if (toId(m?.userId ?? m?.user?.id) === null) return;
    // Skip Otto / system-sent rows — "most recent user who sent the last
    // message" means a real human sender, not an automated response.
    if (m?.systemUserId) return;
    const dir = (m?.direction || "").toUpperCase();
    if (dir === "FROM_OTTO" || (m?.messageType || "").toUpperCase() === "OTTO") return;
    const t = time(m);
    if (best === null) {
      best = { m, index, t };
      return;
    }
    // A real timestamp ALWAYS beats a missing one; between two real
    // timestamps the later wins; array position is only a tiebreak when
    // NEITHER has a timestamp (order is not guaranteed — the list can be
    // newest-first — so a null-timestamp row must never override a real one).
    if (t !== null && best.t !== null) {
      if (t >= best.t) best = { m, index, t };
    } else if (t !== null && best.t === null) {
      best = { m, index, t };
    } else if (t === null && best.t === null && index >= best.index) {
      best = { m, index, t };
    }
  });
  if (!best) return null;
  // Attributed (display / reply-as) user id — the source of truth for "who the
  // message is from", regardless of what the nested user object holds.
  const attributedId = toId(best.m?.userId ?? best.m?.user?.id);
  if (attributedId === null) return best.m?.user || null;
  const resolved = (Array.isArray(pool) ? pool : []).find(u => u && toId(u.id) === attributedId);
  if (resolved) return resolved;
  const nested = best.m?.user || null;
  if (nested && toId(nested.id) === attributedId) return nested;
  return { id: attributedId };
};

/**
 * Collect assigned opportunity users across the supplied opportunities,
 * ordered primary-type-first then the rest, de-duplicated by user id.
 *
 * @param {Array} leadOpportunities selectedLead.leadOpportunities (or a subset)
 * @returns {Array<{user: Object, userType: Object, isPrimary: boolean}>}
 */
export const getAssignedOpportunityUsers = leadOpportunities => {
  if (!Array.isArray(leadOpportunities)) return [];
  const seen = new Set();
  const primary = [];
  const others = [];
  leadOpportunities.filter(Boolean).forEach(opp => {
    (opp?.leadOpportunityUsers || []).forEach(lou => {
      const id = toId(lou?.user?.id ?? lou?.userId);
      if (id === null || seen.has(id)) return;
      seen.add(id);
      const bucket = isPrimaryUserType(lou?.userType) ? primary : others;
      bucket.push({
        user: lou.user,
        userType: lou.userType,
        // Opportunity user-type id — matched against a saved OPPORTUNITY_USER_TYPE rule.
        userTypeId: toId(lou?.userTypeId ?? lou?.userType?.id),
        isPrimary: bucket === primary,
      });
    });
  });
  return [...primary, ...others];
};

/**
 * Build the ordered, de-duplicated "Reply as" user list.
 *
 * @param {Object}   params
 * @param {Object}   params.loggedInUser    the current user (UserContext)
 * @param {Array}    [params.messages]       conversation messages
 * @param {Array}    [params.leadOpportunities] lead's opportunities (assigned users)
 * @returns {Array} ordered entries (see toEntry); first is always the
 *                  logged-in user when one is supplied.
 */
export const buildReplyAsUsers = ({
  loggedInUser,
  messages = [],
  leadOpportunities = [],
  candidates = [],
} = {}) => {
  const result = [];
  const seen = new Set();

  const push = entry => {
    if (!entry || seen.has(entry.id)) return;
    seen.add(entry.id);
    result.push(entry);
  };

  // Known user records to resolve the most-recent message's attributed userId
  // against (its nested `user` object can be the login sender — see
  // getMostRecentPoster). Assigned users + message authors + the logged-in user
  // + the supplied candidate pool (typically the full userList cache).
  const resolutionPool = [
    loggedInUser,
    ...getAssignedOpportunityUsers(leadOpportunities).map(a => a.user),
    ...(Array.isArray(messages) ? messages : []).map(m => m?.user).filter(Boolean),
    ...(Array.isArray(candidates) ? candidates : []),
  ].filter(Boolean);

  // 1. Logged-in user — always first / default selection.
  push(toEntry(loggedInUser, { contextKey: REPLY_AS_CONTEXT.YOU }));

  // 2. Most recent poster.
  push(
    toEntry(getMostRecentPoster(messages, resolutionPool), { contextKey: REPLY_AS_CONTEXT.RECENT })
  );

  // 3. Assigned opportunity users (primary first).
  getAssignedOpportunityUsers(leadOpportunities).forEach(({ user, userType, isPrimary }) =>
    push(
      toEntry(user, {
        contextKey: REPLY_AS_CONTEXT.ASSIGNED,
        roleName: user?.companyRole?.name || userType?.name || "",
        assignmentType: userType?.name || "",
        isPrimary,
      })
    )
  );

  // 4. Any remaining conversation participants (message authors).
  (Array.isArray(messages) ? messages : []).forEach(m =>
    push(toEntry(m?.user, { contextKey: REPLY_AS_CONTEXT.PARTICIPANT }))
  );

  return result;
};

// First assigned user whose userType.name contains ``token`` (primary-first).
const findAssignedUserByToken = (leadOpportunities, token) => {
  const match = getAssignedOpportunityUsers(leadOpportunities).find(a =>
    (a.userType?.name || "").toLowerCase().includes(token)
  );
  return match?.user || null;
};

const findUserById = (id, pools) => {
  const target = toId(id);
  if (target === null) return null;
  return pools.find(u => u && toId(u.id) === target) || null;
};

const contextForRule = rule => {
  if (rule === REPLY_RULE.ME) return REPLY_AS_CONTEXT.YOU;
  if (rule === REPLY_RULE.RECENT) return REPLY_AS_CONTEXT.RECENT;
  return REPLY_AS_CONTEXT.ASSIGNED;
};

/**
 * Resolve the concrete user a reply should be signed as, per the manager's
 * default rule and any per-lead override (EAI-1530). Resolution priority:
 *
 *   per-lead override  →  default rule  →  role resolution  →  actual sender
 *
 * Every branch falls back gracefully to the logged-in manager when the target
 * can't be resolved (missing/inactive/deleted assignee, empty assignment).
 *
 * @returns {{ entry: Object|null, source: string }} entry is a normalized
 *   reply-as entry (see toEntry); source is "override" | <rule> | "fallback".
 */
export const resolveReplyAsUser = ({
  rule = REPLY_RULE.ME,
  overrideUserId = null,
  loggedInUser,
  messages = [],
  leadOpportunities = [],
  candidates = [],
} = {}) => {
  const assignedUsers = getAssignedOpportunityUsers(leadOpportunities).map(a => a.user);
  const messageUsers = (Array.isArray(messages) ? messages : []).map(m => m?.user).filter(Boolean);
  const pool = [loggedInUser, ...assignedUsers, ...messageUsers, ...candidates].filter(Boolean);

  // 1. Per-lead override wins when it points at a resolvable user.
  const overrideUser = findUserById(overrideUserId, pool);
  if (overrideUser) {
    return {
      entry: toEntry(overrideUser, { contextKey: REPLY_AS_CONTEXT.ASSIGNED }),
      source: "override",
    };
  }

  // 2/3. Default rule → role resolution.
  let resolved = null;
  if (rule === REPLY_RULE.ME) resolved = loggedInUser;
  else if (rule === REPLY_RULE.RECENT) resolved = getMostRecentPoster(messages, pool);
  else if (RULE_ROLE_TOKEN[rule])
    resolved = findAssignedUserByToken(leadOpportunities, RULE_ROLE_TOKEN[rule]);

  if (resolved && toId(resolved.id) !== null) {
    return { entry: toEntry(resolved, { contextKey: contextForRule(rule) }), source: rule };
  }

  // 4. Graceful fallback → the logged-in manager (actual sender).
  return {
    entry: toEntry(loggedInUser, { contextKey: REPLY_AS_CONTEXT.YOU }),
    source: "fallback",
  };
};

/** Row "kind" — drives name/subtitle/tooltip presentation in the popup. */
export const REPLY_ROW_KIND = Object.freeze({
  ME: "me",
  RECENT: "recent",
  ROLE: "role",
});

/**
 * Build the manager "Reply As" popup rows (EAI-1530, client-final design).
 *
 * Rows are the ACTUAL people you can reply as for this lead — Me, the most
 * recent poster, then every assigned opportunity user (primary-first) and any
 * remaining conversation participants — each labeled by their real role
 * (companyRole), so a dealership's true roles (BDC, Sales Manager, Technician,
 * Service Advisor, …) are shown as they exist rather than a fixed enum.
 * De-duplicated so each person appears once at their highest-priority slot.
 *
 * Data-only (no i18n/presentation): the component owns label wording. Each row:
 *   { key, kind, entry, role, location }
 *   - key      stable id for React + "preferred" selection
 *              ("ME" | "RECENT" | "USER:<id>")
 *   - kind     REPLY_ROW_KIND (me | recent | role)
 *   - role     companyRole name to display
 *   - location store name (ME row only)
 *
 * @param {Object} params  loggedInUser, messages, leadOpportunities, locationName
 * @returns {Array<{key,kind,entry,role,location}>}
 */
export const buildReplyRuleRows = ({
  loggedInUser,
  messages = [],
  leadOpportunities = [],
  locationName = "",
} = {}) =>
  buildReplyAsUsers({ loggedInUser, messages, leadOpportunities }).map(entry => {
    let kind = REPLY_ROW_KIND.ROLE;
    if (entry.contextKey === REPLY_AS_CONTEXT.YOU) kind = REPLY_ROW_KIND.ME;
    else if (entry.contextKey === REPLY_AS_CONTEXT.RECENT) kind = REPLY_ROW_KIND.RECENT;
    const key =
      kind === REPLY_ROW_KIND.ME
        ? "ME"
        : kind === REPLY_ROW_KIND.RECENT
        ? "RECENT"
        : `USER:${entry.id}`;
    return {
      key,
      kind,
      entry,
      role: entry.roleName || "",
      // Assignment context ("BDC", "Primary Assigned") → "{role} - {assignmentType}".
      assignmentType: entry.assignmentType || "",
      // The ME row shows the manager's own store next to the role
      // ("Store Manager - Woods Cross"); other rows carry no location.
      location: kind === REPLY_ROW_KIND.ME ? locationName || "" : "",
    };
  });

/**
 * Resolve the current signer from the saved default rule + per-lead override
 * (EAI-1530). Fallback sequence per spec:
 *
 *   per-lead override
 *     → saved rule: SELF | MOST_RECENT | OPPORTUNITY_USER_TYPE(id)
 *       → if the rule can't be applied (role not assigned / user missing)
 *         → MOST_RECENT
 *           → SELF (logged-in user)
 *
 * @param {Object} params
 * @param {string} [params.ruleType]              REPLY_RULE_TYPE value
 * @param {number} [params.opportunityUserTypeId] only for OPPORTUNITY_USER_TYPE
 * @param {number} [params.overrideUserId]        per-lead override
 * @returns {{ entry: Object|null, source: "override"|"role"|"most_recent"|"self" }}
 */
export const resolveSignerByRole = ({
  ruleType,
  opportunityUserTypeId = null,
  // The saved user-type's NAME (e.g. "Primary Assigned"). getOpportunityUserTypes
  // ids and leadOpportunityUsers[].userTypeId can live in different id spaces, so
  // we match on id OR name — the name is the reliable cross-reference.
  opportunityUserTypeName = "",
  overrideUserId = null,
  loggedInUser,
  messages = [],
  leadOpportunities = [],
  candidates = [],
} = {}) => {
  const assigned = getAssignedOpportunityUsers(leadOpportunities);
  const pool = [
    loggedInUser,
    ...assigned.map(a => a.user),
    ...(Array.isArray(messages) ? messages : []).map(m => m?.user).filter(Boolean),
    ...candidates,
  ].filter(Boolean);

  const self = () => ({
    entry: toEntry(loggedInUser, { contextKey: REPLY_AS_CONTEXT.YOU }),
    source: "self",
  });
  // MOST_RECENT with SELF fallback (Priority 2 → Priority 3).
  const mostRecent = () => {
    const r = getMostRecentPoster(messages, pool);
    if (r && toId(r.id) !== null) {
      return { entry: toEntry(r, { contextKey: REPLY_AS_CONTEXT.RECENT }), source: "most_recent" };
    }
    return self();
  };

  // Per-lead override always wins.
  const overrideUser = findUserById(overrideUserId, pool);
  if (overrideUser) {
    return {
      entry: toEntry(overrideUser, { contextKey: REPLY_AS_CONTEXT.ASSIGNED }),
      source: "override",
    };
  }

  // No saved rule or SELF → the logged-in user.
  if (!ruleType || ruleType === REPLY_RULE_TYPE.SELF) return self();

  // MOST_RECENT → last human sender (else self).
  if (ruleType === REPLY_RULE_TYPE.MOST_RECENT) return mostRecent();

  // OPPORTUNITY_USER_TYPE → assigned user with that userType (by id OR name;
  // else most recent → self).
  if (ruleType === REPLY_RULE_TYPE.OPPORTUNITY_USER_TYPE) {
    const targetId = toId(opportunityUserTypeId);
    const targetName = String(opportunityUserTypeName || "")
      .trim()
      .toLowerCase();
    const match = assigned.find(a => {
      const byId = targetId !== null && a.userTypeId === targetId;
      const byName = targetName && (a.userType?.name || "").trim().toLowerCase() === targetName;
      return byId || byName;
    });
    if (match?.user && toId(match.user.id) !== null) {
      return {
        entry: toEntry(match.user, {
          contextKey: REPLY_AS_CONTEXT.ASSIGNED,
          assignmentType: match.userType?.name || "",
        }),
        source: "role",
      };
    }
    return mostRecent();
  }

  return self();
};
